/**
 * ExtraPaintingAdminPage — Iter219.
 *
 * Office-level catalog editor for Extra Painting items (Recoat Fascia,
 * Garage Doors, Gutters, …). Operators pick from this list in the
 * Painting Quote Builder. Pricing follows the 4-rate model from the
 * source spreadsheet:
 *
 *   - buy_cost          $/unit  cost-of-goods (paint itself)
 *   - materials_rate    $/unit  what customer pays for materials
 *   - labour_rate       $/unit  what customer pays for labour
 *   - contractor_rate   $/unit  what we pay the sub-contractor
 *
 * Customer-facing sell price = materials_rate + labour_rate.
 *
 * Backed by `/api/extra-painting-items` CRUD + `/seed`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Trash2, RotateCcw, Paintbrush, ShieldAlert } from 'lucide-react';

const BLANK_ROW = {
  name: '',
  unit: 'lm',
  buy_cost: 0,
  materials_rate: 0,
  labour_rate: 0,
  contractor_rate: 0,
  labour_code: 'Painter',
  supplier: 'BondLast',
  labour_recipe: 'G.Primer(10m2/L):T.Coat(10L/m2)',
  sort_order: 99,
};

const UNITS = [
  { label: 'Linear m (/mtr)', value: 'lm' },
  { label: 'Each',            value: 'each' },
  { label: 'm²',              value: 'm2' },
];

function dirty(a, b) {
  // Compare only editable fields
  const fields = ['name','unit','buy_cost','materials_rate','labour_rate','contractor_rate','labour_code','supplier','labour_recipe','sort_order'];
  return fields.some(f => String(a[f] ?? '') !== String(b[f] ?? ''));
}

export default function ExtraPaintingAdminPage() {
  const { api, user } = useAuth();
  const isAdmin = (user?.role || '') === 'admin';

  const [items, setItems] = useState([]);            // server snapshot
  const [drafts, setDrafts] = useState({});           // id → working copy
  const [newRow, setNewRow] = useState(BLANK_ROW);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Effect-driven initial + manual reload (bumping reloadKey re-runs).
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    api.get('/extra-painting-items')
      .then(({ data }) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        const next = {};
        arr.forEach(it => { next[it.id] = { ...it }; });
        setItems(arr);
        setDrafts(next);
      })
      .catch(e => {
        if (!cancelled) toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const setDraftField = (id, field, value) => {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const setNewField = (field, value) => {
    setNewRow(prev => ({ ...prev, [field]: value }));
  };

  const saveRow = async (id) => {
    const draft = drafts[id];
    if (!draft) return;
    if (!draft.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSavingId(id);
    try {
      const payload = {
        name: draft.name,
        unit: draft.unit,
        buy_cost:        Number(draft.buy_cost) || 0,
        materials_rate:  Number(draft.materials_rate) || 0,
        labour_rate:     Number(draft.labour_rate) || 0,
        contractor_rate: Number(draft.contractor_rate) || 0,
        labour_code:   draft.labour_code,
        supplier:      draft.supplier,
        labour_recipe: draft.labour_recipe,
        sort_order:    Number(draft.sort_order) || 0,
      };
      const { data } = await api.put(`/extra-painting-items/${id}`, payload);
      setItems(prev => prev.map(it => it.id === id ? data : it));
      setDrafts(prev => ({ ...prev, [id]: { ...data } }));
      toast.success(`Saved "${data.name}"`);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setSavingId(id);
    try {
      await api.delete(`/extra-painting-items/${id}`);
      setItems(prev => prev.filter(it => it.id !== id));
      setDrafts(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success(`Deleted "${name}"`);
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const createRow = async () => {
    if (!newRow.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...newRow,
        buy_cost:        Number(newRow.buy_cost) || 0,
        materials_rate:  Number(newRow.materials_rate) || 0,
        labour_rate:     Number(newRow.labour_rate) || 0,
        contractor_rate: Number(newRow.contractor_rate) || 0,
        sort_order:      Number(newRow.sort_order) || 0,
      };
      const { data } = await api.post('/extra-painting-items', payload);
      setItems(prev => [...prev, data]);
      setDrafts(prev => ({ ...prev, [data.id]: { ...data } }));
      setNewRow(BLANK_ROW);
      toast.success(`Added "${data.name}"`);
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm('Reseed the catalog from the spreadsheet defaults? Existing items with matching names will be left alone.')) return;
    setSeeding(true);
    try {
      const { data } = await api.post('/extra-painting-items/seed');
      toast.success(`Seed complete — ${data.inserted} added, ${data.skipped.length} already existed.`);
      reload();
    } catch (e) {
      toast.error(`Seed failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  const sorted = useMemo(() => {
    return [...items].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)
    );
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-md p-4 text-sm" data-testid="extra-painting-admin-locked">
          <ShieldAlert size={16} className="inline mr-2" />
          Admin role required to edit the Extra Painting catalog.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="extra-painting-admin-page" className="space-y-4">
      <div data-testid="extra-painting-retired-banner" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <strong>Retired for the Recoat builder.</strong> Extras (garage doors, gutters, fascia, fences) are now priced from
        {' '}<a href="/component-rates?component=recoat" className="underline font-semibold">Component Rate Tables → Re-Coat</a>.
        {' '}This list is kept only for older Restoration picks.
      </div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
            <Paintbrush size={24} className="text-amber-600" /> Extra Painting Items
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
            Catalog of non-roof painting add-ons (Recoat Fascia, Garage Doors, Gutters, …). Operators
            pick from this list inside the <strong>Painting Quote Builder</strong>. Customer-facing sell
            price per unit = <code>materials_rate + labour_rate</code>.
          </p>
        </div>
        <button
          type="button"
          data-testid="extra-painting-reseed-btn"
          onClick={reseed}
          disabled={seeding}
          className="text-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md px-3 py-1.5 flex items-center gap-1 disabled:opacity-60"
        >
          {seeding ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
          Reseed defaults
        </button>
      </div>

      {/* Catalog table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="extra-painting-admin-table">
        <div className="bg-slate-900 text-white text-xs uppercase tracking-wide grid grid-cols-12 gap-2 px-3 py-2">
          <div className="col-span-3">Name</div>
          <div className="col-span-1 text-center">Unit</div>
          <div className="col-span-1 text-right">Buy Cost</div>
          <div className="col-span-1 text-right">Materials</div>
          <div className="col-span-1 text-right">Labour</div>
          <div className="col-span-1 text-right">Contractor</div>
          <div className="col-span-2">Supplier / Recipe</div>
          <div className="col-span-1 text-right">Sort</div>
          <div className="col-span-1 text-right"> </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="extra-painting-admin-loading">
            <Loader2 size={14} className="animate-spin" /> Loading catalog…
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="extra-painting-admin-empty">
            Catalog is empty. Hit <strong>Reseed defaults</strong> above to load the 4 baseline items
            from the spreadsheet, or add your own below.
          </div>
        ) : sorted.map((it, idx) => {
          const draft = drafts[it.id] || it;
          const isDirty = dirty(draft, it);
          const sell = (Number(draft.materials_rate) || 0) + (Number(draft.labour_rate) || 0);
          return (
            <div key={it.id} data-testid={`extra-painting-admin-row-${idx}`}
                 className={`grid grid-cols-12 gap-2 px-3 py-2 items-start border-t border-slate-100 ${isDirty ? 'bg-amber-50/30' : ''}`}>
              <div className="col-span-3">
                <input
                  type="text"
                  value={draft.name ?? ''}
                  onChange={e => setDraftField(it.id, 'name', e.target.value)}
                  data-testid={`extra-painting-name-${idx}`}
                  className="w-full text-sm px-2 py-1 border border-slate-300 rounded"
                />
                <div className="text-[10px] text-emerald-700 mt-0.5">
                  Sell: <strong>${sell.toFixed(2)}</strong> / {draft.unit}
                </div>
              </div>
              <div className="col-span-1">
                <select
                  value={draft.unit}
                  onChange={e => setDraftField(it.id, 'unit', e.target.value)}
                  data-testid={`extra-painting-unit-${idx}`}
                  className="w-full text-sm px-1 py-1 border border-slate-300 rounded bg-white"
                >
                  {UNITS.map(u => <option key={u.value} value={u.value}>{u.value}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <input type="number" step="0.01" min="0"
                  value={draft.buy_cost ?? 0}
                  onChange={e => setDraftField(it.id, 'buy_cost', e.target.value)}
                  data-testid={`extra-painting-buy-cost-${idx}`}
                  className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
                />
              </div>
              <div className="col-span-1">
                <input type="number" step="0.01" min="0"
                  value={draft.materials_rate ?? 0}
                  onChange={e => setDraftField(it.id, 'materials_rate', e.target.value)}
                  data-testid={`extra-painting-materials-rate-${idx}`}
                  className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
                />
              </div>
              <div className="col-span-1">
                <input type="number" step="0.01" min="0"
                  value={draft.labour_rate ?? 0}
                  onChange={e => setDraftField(it.id, 'labour_rate', e.target.value)}
                  data-testid={`extra-painting-labour-rate-${idx}`}
                  className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
                />
              </div>
              <div className="col-span-1">
                <input type="number" step="0.01" min="0"
                  value={draft.contractor_rate ?? 0}
                  onChange={e => setDraftField(it.id, 'contractor_rate', e.target.value)}
                  data-testid={`extra-painting-contractor-rate-${idx}`}
                  className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <input type="text"
                  value={draft.supplier ?? ''}
                  onChange={e => setDraftField(it.id, 'supplier', e.target.value)}
                  data-testid={`extra-painting-supplier-${idx}`}
                  placeholder="Supplier"
                  className="w-full text-xs px-2 py-1 border border-slate-300 rounded"
                />
                <input type="text"
                  value={draft.labour_recipe ?? ''}
                  onChange={e => setDraftField(it.id, 'labour_recipe', e.target.value)}
                  data-testid={`extra-painting-recipe-${idx}`}
                  placeholder="Labour recipe"
                  className="w-full text-xs px-2 py-1 border border-slate-300 rounded font-mono"
                />
              </div>
              <div className="col-span-1">
                <input type="number" step="1"
                  value={draft.sort_order ?? 0}
                  onChange={e => setDraftField(it.id, 'sort_order', e.target.value)}
                  data-testid={`extra-painting-sort-${idx}`}
                  className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
                />
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button type="button"
                  disabled={!isDirty || savingId === it.id}
                  onClick={() => saveRow(it.id)}
                  data-testid={`extra-painting-save-${idx}`}
                  title={isDirty ? 'Save changes' : 'No unsaved changes'}
                  className={`p-1.5 rounded ${isDirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                >
                  {savingId === it.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                </button>
                <button type="button"
                  onClick={() => deleteRow(it.id, it.name)}
                  data-testid={`extra-painting-delete-${idx}`}
                  className="p-1.5 rounded text-rose-600 hover:bg-rose-50"
                  title="Delete item"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create new row */}
      <div className="bg-white border border-amber-200 rounded-md p-3" data-testid="extra-painting-admin-new">
        <h3 className="text-sm font-bold text-amber-900 flex items-center gap-1 mb-2 font-[Manrope]">
          <Plus size={14} /> Add new item
        </h3>
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Name</label>
            <input type="text"
              value={newRow.name}
              onChange={e => setNewField('name', e.target.value)}
              placeholder="e.g. Recoat Front Door"
              data-testid="extra-painting-new-name"
              className="w-full text-sm px-2 py-1 border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Unit</label>
            <select
              value={newRow.unit}
              onChange={e => setNewField('unit', e.target.value)}
              data-testid="extra-painting-new-unit"
              className="w-full text-sm px-1 py-1 border border-slate-300 rounded bg-white"
            >
              {UNITS.map(u => <option key={u.value} value={u.value}>{u.value}</option>)}
            </select>
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Buy</label>
            <input type="number" step="0.01" min="0"
              value={newRow.buy_cost}
              onChange={e => setNewField('buy_cost', e.target.value)}
              data-testid="extra-painting-new-buy-cost"
              className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Mat.</label>
            <input type="number" step="0.01" min="0"
              value={newRow.materials_rate}
              onChange={e => setNewField('materials_rate', e.target.value)}
              data-testid="extra-painting-new-materials"
              className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Lab.</label>
            <input type="number" step="0.01" min="0"
              value={newRow.labour_rate}
              onChange={e => setNewField('labour_rate', e.target.value)}
              data-testid="extra-painting-new-labour"
              className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sub.</label>
            <input type="number" step="0.01" min="0"
              value={newRow.contractor_rate}
              onChange={e => setNewField('contractor_rate', e.target.value)}
              data-testid="extra-painting-new-contractor"
              className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-2 space-y-1">
            <input type="text"
              value={newRow.supplier}
              onChange={e => setNewField('supplier', e.target.value)}
              placeholder="Supplier"
              data-testid="extra-painting-new-supplier"
              className="w-full text-xs px-2 py-1 border border-slate-300 rounded"
            />
            <input type="text"
              value={newRow.labour_recipe}
              onChange={e => setNewField('labour_recipe', e.target.value)}
              placeholder="Labour recipe"
              data-testid="extra-painting-new-recipe"
              className="w-full text-xs px-2 py-1 border border-slate-300 rounded font-mono"
            />
          </div>
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sort</label>
            <input type="number" step="1"
              value={newRow.sort_order}
              onChange={e => setNewField('sort_order', e.target.value)}
              data-testid="extra-painting-new-sort"
              className="w-full text-sm px-2 py-1 text-right border border-slate-300 rounded"
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <button type="button"
              onClick={createRow}
              disabled={creating || !newRow.name.trim()}
              data-testid="extra-painting-new-add-btn"
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded px-3 py-1.5 flex items-center gap-1"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        💡 Operators add these items from the Painting Quote Builder under <strong>Extra Painting Add-ons</strong>.
        Customer-facing sell price per unit is computed as <code>materials_rate + labour_rate</code>; the buy cost +
        contractor rate are stored for job-cost reporting later.
      </p>
    </div>
  );
}
